import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useNorseTerm } from './NorseTermData'
import { useSteps } from './ChecklistView'
import { stepHref } from './checklist'
import { termDay } from './attendanceLogic'
import { fmtDate } from './Overview'

/**
 * The top of Norse Term: when it is, the deadlines that matter to this
 * person, and how far away it is. Black, with one ring of red, so the page
 * has a clear first thing to read.
 *
 * `title` makes "Norse Term" the page's h1 (the Norse Term home); on the app
 * home it is a label above the greeting's page.
 */
export function TermBanner({ title = false }: { title?: boolean }) {
  const { term, roles, phase } = useNorseTerm()
  const today = termDay(term)
  const daysTo = term.startDate
    ? Math.ceil((new Date(term.startDate + 'T00:00:00').getTime() - new Date(new Date().toDateString()).getTime()) / 86_400_000)
    : null
  const upcoming = (iso: string | undefined) => !!iso && new Date(iso + 'T23:59:59').getTime() >= Date.now()
  const d = term.deadlines
  // Only the deadlines this person works to, soonest first.
  const lines = [
    (roles.teach || roles.review) && upcoming(d.courses) && { iso: d.courses, text: 'Course proposals due' },
    (roles.student || roles.review || roles.run || roles.seminar) && upcoming(d.proposals) && { iso: d.proposals, text: 'Project proposals due' },
    (roles.student || roles.run || roles.review) && upcoming(d.rankings) && { iso: d.rankings, text: 'Course selections due' },
  ].filter(Boolean) as Array<{ iso: string; text: string }>
  lines.sort((a, b) => a.iso.localeCompare(b.iso))

  const Eyebrow = title ? 'h1' : 'div'
  return (
    <section className="banner" aria-label="Norse Term">
      <div className="banner__main">
        <Eyebrow className="banner__eyebrow">Norse Term</Eyebrow>
        <p className="banner__dates">{term.weekLabel || 'Dates to be set'}</p>
        {lines.length > 0 && (
          <ul className="banner__dl">
            {lines.map((l) => <li key={l.text}>{l.text} <strong>{fmtDate(l.iso, { month: 'short', day: 'numeric' })}</strong></li>)}
          </ul>
        )}
      </div>
      {phase === 'prep' && daysTo !== null && daysTo > 0 && (
        <div className="banner__count"><b>{daysTo}</b><span>{daysTo === 1 ? 'day to go' : 'days to go'}</span></div>
      )}
      {today !== null && (
        <div className="banner__count"><b>{today === 3 ? 'Fri' : `Day ${today + 1}`}</b><span>{today === 3 ? 'open house' : 'of the week'}</span></div>
      )}
    </section>
  )
}

/** The one thing to do next, from the person's own steps: the page's call to action. */
export function NextUp() {
  const { sections, current } = useSteps()
  if (!current) return null
  const section = sections.find((s) => s.items.includes(current))
  const to = stepHref(current)
  return (
    <section className="nextup" aria-label="Your next step">
      <div className="nextup__main">
        <div className="nextup__eyebrow">Next up{section && sections.length > 1 ? ` · ${section.title}` : ''}</div>
        <h2 className="nextup__title"><Link to={to}>{current.label}</Link></h2>
        {current.detail && <p className="nextup__detail">{current.detail}</p>}
      </div>
      <Link to={to} className="btn nextup__go" tabIndex={-1} aria-hidden="true">
        Continue <ArrowRight size={17} aria-hidden="true" />
      </Link>
    </section>
  )
}
